import React, { FC, useEffect, useState } from 'react';

import {
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
} from '@chakra-ui/react';

import {
  PURCHASE_ORDER_SEND_BCC_EMAILS,
  PURCHASE_ORDER_SEND_CC_EMAILS,
} from 'constants/purchaseOrder';
import { PurchaseOrder } from 'types/purchaseOrder';
import { formatManufacturerEmails, parseEmailList } from 'utils/manufacturer';
import { getAttachmentDisplayName } from 'utils/s3';

type SendPurchaseOrderDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  purchaseOrder?: PurchaseOrder;
  manufacturerName?: string;
  defaultManufacturerEmails?: string | string[];
  washHubName?: string;
  defaultWashHubEmail?: string;
  isSending: boolean;
  onSend: (payload: {
    toEmail: string[];
    attachmentKeys: string[];
    emailBody: string;
    subject: string;
  }) => void;
};

const getDefaultEmailSubject = (poNumber?: string) =>
  `Purchase Order ${poNumber || ''}`.trim();

const getDefaultEmailBody = (poNumber?: string) =>
  `Please find purchase order ${poNumber || ''} attached.`;

const SendPurchaseOrderDialog: FC<SendPurchaseOrderDialogProps> = ({
  isOpen,
  onClose,
  purchaseOrder,
  manufacturerName,
  defaultManufacturerEmails,
  washHubName,
  defaultWashHubEmail,
  isSending,
  onSend,
}) => {
  const attachments = purchaseOrder?.attachments ?? [];
  const [manufacturerEmailsInput, setManufacturerEmailsInput] = useState('');
  const [washHubEmail, setWashHubEmail] = useState(defaultWashHubEmail ?? '');
  const [selectedKeys, setSelectedKeys] = useState<string[]>(attachments);
  const [subject, setSubject] = useState(
    getDefaultEmailSubject(purchaseOrder?.poNumer),
  );
  const [emailBody, setEmailBody] = useState(
    getDefaultEmailBody(purchaseOrder?.poNumer),
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setManufacturerEmailsInput(
      formatManufacturerEmails(defaultManufacturerEmails),
    );
    setWashHubEmail(defaultWashHubEmail ?? '');
    setSelectedKeys(purchaseOrder?.attachments ?? []);
    setSubject(getDefaultEmailSubject(purchaseOrder?.poNumer));
    setEmailBody(getDefaultEmailBody(purchaseOrder?.poNumer));
  }, [
    isOpen,
    defaultManufacturerEmails,
    defaultWashHubEmail,
    purchaseOrder?._id,
  ]);

  const toEmail = Array.from(
    new Set([
      ...parseEmailList(manufacturerEmailsInput),
      ...parseEmailList(washHubEmail),
    ]),
  );

  const canSend = toEmail.length > 0 && selectedKeys.length > 0 && !isSending;

  return (
    <Modal isCentered isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent borderRadius="10px">
        <ModalCloseButton isDisabled={isSending} _focus={{ outline: 'none' }} />
        <ModalHeader mt={4}>Send Purchase Order</ModalHeader>
        <ModalBody>
          <FormControl mb={4}>
            <FormLabel fontSize="12px" color="#afafaf">
              Manufacturer
            </FormLabel>
            <Text>{manufacturerName || '-'}</Text>
          </FormControl>

          <FormControl mb={4} isRequired>
            <FormLabel fontSize="12px" color="#afafaf">
              Manufacturer Email(s)
            </FormLabel>
            <Input
              value={manufacturerEmailsInput}
              placeholder="orders@example.com, billing@example.com"
              isDisabled={isSending}
              onChange={(event) =>
                setManufacturerEmailsInput(event.target.value)
              }
            />
          </FormControl>

          <FormControl mb={4}>
            <FormLabel fontSize="12px" color="#afafaf">
              Destination Hub
            </FormLabel>
            <Text>{washHubName || purchaseOrder?.location || '-'}</Text>
          </FormControl>

          <FormControl mb={4}>
            <FormLabel fontSize="12px" color="#afafaf">
              Destination Hub Email (Separate by comma)
            </FormLabel>
            <Input
              value={washHubEmail}
              isDisabled={isSending}
              onChange={(event) => setWashHubEmail(event.target.value)}
            />
          </FormControl>

          <FormControl mb={4}>
            <FormLabel fontSize="12px" color="#afafaf">
              Subject
            </FormLabel>
            <Input
              value={subject}
              isDisabled={isSending}
              onChange={(event) => setSubject(event.target.value)}
            />
          </FormControl>

          <FormControl mb={4}>
            <FormLabel fontSize="12px" color="#afafaf">
              Email Body
            </FormLabel>
            <Textarea
              value={emailBody}
              minH="120px"
              isDisabled={isSending}
              onChange={(event) => setEmailBody(event.target.value)}
            />
          </FormControl>

          <FormControl mb={4}>
            <FormLabel fontSize="12px" color="#afafaf">
              Attachments
            </FormLabel>
            {attachments.length === 0 ? (
              <Text fontSize="sm" color="gray.500">
                No attachments available to send.
              </Text>
            ) : (
              <Flex direction="column" gap={2}>
                {attachments.map((attachment) => (
                  <Checkbox
                    key={attachment}
                    isChecked={selectedKeys.includes(attachment)}
                    isDisabled={isSending}
                    onChange={(event) => {
                      if (event.target.checked) {
                        setSelectedKeys((current) => [...current, attachment]);
                      } else {
                        setSelectedKeys((current) =>
                          current.filter((key) => key !== attachment),
                        );
                      }
                    }}>
                    {getAttachmentDisplayName(attachment)}
                  </Checkbox>
                ))}
              </Flex>
            )}
          </FormControl>

          {PURCHASE_ORDER_SEND_CC_EMAILS.length > 0 ? (
            <Text fontSize="sm" color="gray.500">
              CC: {PURCHASE_ORDER_SEND_CC_EMAILS.join(', ')}
            </Text>
          ) : null}
          {PURCHASE_ORDER_SEND_BCC_EMAILS.length > 0 ? (
            <Text fontSize="sm" color="gray.500">
              BCC: {PURCHASE_ORDER_SEND_BCC_EMAILS.join(', ')}
            </Text>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button
            variant="outline"
            mr={3}
            isDisabled={isSending}
            onClick={onClose}>
            Cancel
          </Button>
          <Button
            isLoading={isSending}
            isDisabled={!canSend}
            onClick={() =>
              onSend({
                toEmail,
                attachmentKeys: selectedKeys,
                subject: subject.trim(),
                emailBody: emailBody.trim(),
              })
            }>
            Send
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SendPurchaseOrderDialog;
